/**
 * MagicLinkSendModal — UI interim ADR-013 para despachar Magic Links via `wa.me`.
 *
 * Flow real D2 (alineado con manual-dispatch-wame.spec.ts AC-26):
 *   1. Modal abre (open=true). Estado inicial: picker de purpose + botón "Emitir".
 *   2. Staff selecciona purpose (buttons `mlm-purpose-<purpose>`) y click "Emitir".
 *   3. POST /api/magic-links/issue body `{patientId, purpose}` → response incluye
 *      `manual_dispatch.wa_me_url` + `whatsapp_message` canónico + `expires_at`.
 *   4. Preview + banner "Modo temporal" + textarea readOnly + countdown MM:SS.
 *   5. Click "Abrir WhatsApp" → window.open(wa_me_url) + POST /api/magic-links/:id/confirm-dispatch.
 *   6. Click "Copiar mensaje" → navigator.clipboard.writeText(whatsapp_message).
 *
 * Anti-PHI compliance:
 *   - Mensaje canónico NO editable (regla AC-26 — anti-PHI leak).
 *   - Phone solo se muestra como "****5678" (últimos 4 dígitos).
 *   - `token_plain` + `magic_url` + `whatsapp_message` NUNCA se loggean ni persisten en storage.
 *   - Al cerrar modal: reset de todo el estado interno (no caché PHI).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ExternalLink, Copy, MessageCircle, Clock, AlertTriangle, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { api, ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import type {
  IssueMagicLinkRequest,
  IssueMagicLinkResponse,
  MagicLinkPurpose,
} from '@/lib/types/fitlab-fhir'

// F7 — descriptor de cuestionario para el dropdown cuando purpose=form_fill.
interface FormCatalogItem {
  code: string
  title: string
  audience: 'worker' | 'staff'
  subtitle?: string
}

interface MagicLinkSendModalProps {
  patientId: string
  /** Solo first name — para UX de preview ("Enviar a Juan"). */
  patientName: string
  /**
   * Últimos 4 dígitos del teléfono. PHI reducido — nunca teléfono completo en UI.
   * Ej: `"5678"` → render `"****5678"`.
   */
  patientPhoneLast4: string
  /** Purpose default (el usuario puede cambiarlo antes de emitir). */
  purpose?: MagicLinkPurpose
  open: boolean
  onClose: () => void
}

type Status = 'picking' | 'issuing' | 'ready' | 'confirmed' | 'error'

const PURPOSE_OPTIONS: Array<{ value: MagicLinkPurpose; label: string }> = [
  { value: 'results_view', label: 'Ver resultados' },
  { value: 'form_fill', label: 'Completar formulario' },
  { value: 'alarm_action', label: 'Responder alarma' },
  { value: 'consent_confirm', label: 'Confirmar consentimiento' },
]

/**
 * Formatea milisegundos restantes a `MM:SS`. Si ≤0 → `00:00`.
 */
function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00'
  const totalSec = Math.floor(ms / 1000)
  const mm = Math.floor(totalSec / 60)
    .toString()
    .padStart(2, '0')
  const ss = (totalSec % 60).toString().padStart(2, '0')
  return `${mm}:${ss}`
}

export function MagicLinkSendModal({
  patientId,
  patientName,
  patientPhoneLast4,
  purpose: initialPurpose = 'results_view',
  open,
  onClose,
}: MagicLinkSendModalProps) {
  const { show } = useToast()
  const [selectedPurpose, setSelectedPurpose] = useState<MagicLinkPurpose>(initialPurpose)
  const [status, setStatus] = useState<Status>('picking')
  const [response, setResponse] = useState<IssueMagicLinkResponse | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [countdownMs, setCountdownMs] = useState<number>(0)
  // F7 — questionnaire seleccionado cuando purpose=form_fill.
  const [questionnaireCode, setQuestionnaireCode] = useState<string>('epworth')

  // F7 — catálogo cuestionarios disponibles (worker-facing). Filtrado en cliente.
  const formsCatalogQuery = useQuery({
    queryKey: ['forms-catalog-worker'],
    enabled: open && selectedPurpose === 'form_fill',
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const list = await api.get<{ codes: string[] }>('/api/forms', { anonymous: true })
      const items = await Promise.all(
        list.codes.map(async (code) => {
          try {
            const def = await api.get<FormCatalogItem>(`/api/forms/${code}`, { anonymous: true })
            return def
          } catch {
            return null
          }
        }),
      )
      return items.filter((d): d is FormCatalogItem => !!d && d.audience === 'worker')
    },
  })

  // Mutation emisión magic link — invocada al click "Emitir".
  const issueMutation = useMutation({
    mutationFn: async (payload: IssueMagicLinkRequest & { questionnaire_code?: string }) =>
      api.post<IssueMagicLinkResponse>('/api/magic-links/issue', payload),
    onSuccess: (data) => {
      setResponse(data)
      setStatus('ready')
    },
    onError: (err) => {
      setStatus('error')
      if (err instanceof ApiError && err.status === 429) {
        const scope = (err.body as { scope?: string } | null)?.scope ?? 'patient'
        const msg =
          scope === 'patient'
            ? 'Límite de 5 envíos por hora a este trabajador. Intenta más tarde.'
            : 'Has enviado 10 enlaces en la última hora. Espera antes de enviar más.'
        setErrorMessage(msg)
      } else if (err instanceof ApiError) {
        setErrorMessage('No se pudo generar el enlace.')
      } else {
        setErrorMessage('Error de conexión con el servidor.')
      }
    },
  })

  // Mutation confirm dispatch — invocada al click "Abrir WhatsApp".
  const confirmDispatchMutation = useMutation({
    mutationFn: async (magicLinkId: string) =>
      api.post<{ confirmed_at: string }>(`/api/magic-links/${magicLinkId}/confirm-dispatch`),
  })

  // Reset interno al cerrar modal — evita caché PHI.
  useEffect(() => {
    if (!open) {
      setStatus('picking')
      setResponse(null)
      setErrorMessage(null)
      setCountdownMs(0)
      setSelectedPurpose(initialPurpose)
    }
  }, [open, initialPurpose])

  // Countdown tick cada 1s mientras está abierto y hay expires_at.
  useEffect(() => {
    if (!response?.expires_at || (status !== 'ready' && status !== 'confirmed')) {
      return
    }
    const target = new Date(response.expires_at).getTime()
    const update = () => {
      const remaining = target - Date.now()
      setCountdownMs(remaining)
    }
    update()
    const interval = window.setInterval(update, 1000)
    return () => window.clearInterval(interval)
  }, [response?.expires_at, status])

  // Para TTL largos (form_fill 72h, results_view 24h) un countdown MM:SS no tiene sentido
  // (mostraría "4320:00"). Si quedan >60min mostramos la fecha/hora de expiración humanizada.
  const countdownLabel = useMemo(() => {
    if (response?.expires_at && countdownMs > 60 * 60 * 1000) {
      return new Date(response.expires_at).toLocaleString('es-CL', {
        timeZone: 'America/Santiago',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    }
    return formatCountdown(countdownMs)
  }, [countdownMs, response?.expires_at])
  const expired = countdownMs <= 0 && (status === 'ready' || status === 'confirmed')

  const handleEmit = useCallback(() => {
    setErrorMessage(null)
    setStatus('issuing')
    const payload: IssueMagicLinkRequest & { questionnaire_code?: string } = {
      patientId,
      purpose: selectedPurpose,
    }
    if (selectedPurpose === 'form_fill' && questionnaireCode) {
      payload.questionnaire_code = questionnaireCode
    }
    issueMutation.mutate(payload)
  }, [issueMutation, patientId, selectedPurpose, questionnaireCode])

  const handleOpenWhatsApp = useCallback(() => {
    if (!response?.manual_dispatch?.wa_me_url) return
    window.open(response.manual_dispatch.wa_me_url, '_blank', 'noopener,noreferrer')
    setStatus('confirmed')
    confirmDispatchMutation.mutate(response.magic_link_id)
    show({
      kind: 'success',
      title: 'WhatsApp abierto',
      description: 'Presiona enviar en WhatsApp para completar el envío.',
    })
  }, [response, confirmDispatchMutation, show])

  const handleCopyMessage = useCallback(async () => {
    if (!response?.manual_dispatch?.whatsapp_message) return
    try {
      await navigator.clipboard.writeText(response.manual_dispatch.whatsapp_message)
      show({
        kind: 'success',
        title: 'Mensaje copiado',
        description: 'Pégalo en WhatsApp si el botón no abre la app.',
      })
    } catch {
      show({
        kind: 'error',
        title: 'No se pudo copiar',
        description: 'Copia manualmente el mensaje desde el campo de texto.',
      })
    }
  }, [response, show])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Enviar Magic Link por WhatsApp"
      description={`A ${patientName}`}
      size="md"
    >
      <div className="flex flex-col gap-4" data-testid="magic-link-send-modal" data-status={status}>
        {/* Banner warning permanente — modo interim ADR-013 */}
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
          data-testid="mlm-banner-manual-mode"
          role="note"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            <p className="font-semibold">Modo temporal — envío manual</p>
            <p className="mt-0.5 text-xs">
              Hasta activar el canal oficial FITLAB. No modifiques el mensaje.
            </p>
          </div>
        </div>

        {/* Estado: picking (picker de purpose + botón Emitir) */}
        {status === 'picking' && (
          <div className="flex flex-col gap-3" data-testid="mlm-purpose-picker">
            <p className="text-sm text-slate-700">Selecciona el propósito del enlace:</p>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Propósito">
              {PURPOSE_OPTIONS.map((opt) => {
                const active = selectedPurpose === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSelectedPurpose(opt.value)}
                    className={cn(
                      'inline-flex min-h-11 items-center rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                      active
                        ? 'border-[color:var(--color-fitlab-blue)] bg-[color:var(--color-fitlab-blue)]/10 text-[color:var(--color-fitlab-blue-hover)]'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
                    )}
                    data-testid={`mlm-purpose-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>

            {/* F7 — Selector de cuestionario cuando purpose=form_fill */}
            {selectedPurpose === 'form_fill' && (
              <div className="flex flex-col gap-2" data-testid="mlm-questionnaire-picker">
                <label
                  htmlFor="mlm-questionnaire-select"
                  className="flex items-center gap-1.5 text-sm font-medium text-slate-700"
                >
                  <FileText className="h-4 w-4" aria-hidden /> Formulario a completar
                </label>
                {formsCatalogQuery.isPending && (
                  <p className="text-xs text-slate-500">Cargando catálogo…</p>
                )}
                {formsCatalogQuery.isError && (
                  <p className="text-xs text-red-600">
                    No se pudo cargar el catálogo de formularios.
                  </p>
                )}
                {formsCatalogQuery.data && (
                  <select
                    id="mlm-questionnaire-select"
                    value={questionnaireCode}
                    onChange={(e) => setQuestionnaireCode(e.target.value)}
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm shadow-sm focus:border-[color:var(--color-fitlab-blue)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-fitlab-blue)]/20"
                  >
                    {formsCatalogQuery.data.map((f) => (
                      <option key={f.code} value={f.code}>
                        {f.title}
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-slate-500">
                  El trabajador llegará directo al formulario seleccionado tras abrir el link.
                </p>
              </div>
            )}

            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <p>
                Trabajador: <span className="font-medium text-slate-900">{patientName}</span> ·
                Teléfono <span className="font-mono text-slate-900">****{patientPhoneLast4}</span>
              </p>
            </div>

            <Button
              onClick={handleEmit}
              size="lg"
              className="w-full sm:w-auto"
              data-testid="mlm-emit-button"
            >
              Emitir enlace
            </Button>
          </div>
        )}

        {/* Estado: issuing (loading) */}
        {status === 'issuing' && (
          <div
            className="flex flex-col items-center gap-2 py-6 text-sm text-slate-600"
            data-testid="magic-link-loading"
            role="status"
            aria-live="polite"
          >
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-[color:var(--color-fitlab-blue)]" />
            <p>Generando enlace…</p>
          </div>
        )}

        {/* Estado: error */}
        {status === 'error' && (
          <div
            role="alert"
            className="flex flex-col gap-3 rounded-md border border-[color:var(--color-fitlab-red)]/30 bg-[color:var(--color-fitlab-red)]/5 p-3 text-sm text-[color:var(--color-fitlab-red-hover)]"
            data-testid="magic-link-error-rate-limit"
          >
            <p>{errorMessage ?? 'Error al generar el enlace.'}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setErrorMessage(null)
                setStatus('picking')
              }}
            >
              Volver a intentar
            </Button>
          </div>
        )}

        {/* Estado: ready / confirmed — preview + actions */}
        {response && (status === 'ready' || status === 'confirmed') && (
          <>
            {/* Preview trabajador */}
            <dl
              className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm"
              data-testid="magic-link-preview"
            >
              <div className="flex items-center justify-between py-1">
                <dt className="text-slate-500">Trabajador</dt>
                <dd className="font-medium text-slate-900">{patientName}</dd>
              </div>
              <div className="flex items-center justify-between py-1">
                <dt className="text-slate-500">Teléfono</dt>
                <dd className="font-mono text-slate-900">****{patientPhoneLast4}</dd>
              </div>
              <div className="flex items-center justify-between py-1">
                <dt className="text-slate-500">Vence</dt>
                <dd
                  className={cn(
                    'flex items-center gap-1 font-mono font-semibold',
                    expired ? 'text-[color:var(--color-fitlab-red)]' : 'text-slate-900',
                  )}
                  data-testid="mlm-countdown"
                  aria-live="polite"
                >
                  <Clock className="h-3.5 w-3.5" aria-hidden />
                  {countdownLabel}
                </dd>
              </div>
            </dl>

            {/* Mensaje pre-formateado (read-only, anti-PHI leak) */}
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="mlm-whatsapp-message-textarea"
                className="text-sm font-medium text-slate-800"
              >
                Mensaje que se enviará
              </label>
              <textarea
                id="mlm-whatsapp-message-textarea"
                readOnly
                rows={4}
                value={response.manual_dispatch?.whatsapp_message ?? ''}
                className="w-full resize-none rounded-md border border-slate-300 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700"
                data-testid="mlm-whatsapp-message"
                aria-label="Mensaje pre-formateado no editable"
              />
              <p className="text-xs text-slate-500">
                Mensaje canónico FITLAB. No lo modifiques antes de enviar.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={handleOpenWhatsApp}
                disabled={expired}
                leftIcon={<MessageCircle className="h-4 w-4" aria-hidden />}
                rightIcon={<ExternalLink className="h-4 w-4" aria-hidden />}
                size="lg"
                className="flex-1"
                data-testid="magic-link-open-whatsapp"
              >
                Abrir WhatsApp
              </Button>
              <Button
                onClick={handleCopyMessage}
                variant="outline"
                disabled={expired}
                leftIcon={<Copy className="h-4 w-4" aria-hidden />}
                size="lg"
                data-testid="magic-link-copy-message"
              >
                Copiar mensaje
              </Button>
            </div>

            {status === 'confirmed' && (
              <p
                className="text-center text-xs text-[color:var(--color-fitlab-green)]"
                data-testid="magic-link-confirmed"
              >
                WhatsApp abierto — presiona enviar para completar.
              </p>
            )}

            {expired && (
              <p
                className="text-center text-xs text-[color:var(--color-fitlab-red)]"
                data-testid="magic-link-expired"
              >
                El enlace expiró. Cierra y genera uno nuevo.
              </p>
            )}
          </>
        )}
      </div>
    </Dialog>
  )
}
